/**
 * AdminSpamPage - sender blocklist viewer + add/remove (Phase 12.8).
 *
 * Two sections:
 *   1. Table of sender_blocks (pattern, kind, reason, blocked_by,
 *      created_at, Remove).
 *   2. Add-block form (pattern + kind + reason + Add button).
 *
 * Remove fires a confirm modal first per the work-item brief.
 */
import { useState, type FormEvent } from "react";

import { Alert, Button, Card, Input, Modal, Select, Spinner } from "@/components/ui";
import { SpamBlockRow } from "@/components/admin/SpamBlockRow";
import {
  useAdminSpamBlocks,
  useAddSenderBlock,
  useRemoveSenderBlock,
} from "@/hooks/useAdminSpamBlocks";
import { usePageTitle } from "@/hooks/usePageTitle";
import type { SenderBlockKind, SenderBlockResponse } from "@/types/api";

const KIND_OPTIONS = [
  { value: "email", label: "EMAIL" },
  { value: "domain", label: "DOMAIN" },
];

export function AdminSpamPage(): JSX.Element {
  usePageTitle("Spam blocklist");

  const { data, isLoading, error } = useAdminSpamBlocks();
  const addBlock = useAddSenderBlock();
  const removeBlock = useRemoveSenderBlock();

  const [pattern, setPattern] = useState("");
  const [kind, setKind] = useState<SenderBlockKind>("email");
  const [reason, setReason] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [pendingRemove, setPendingRemove] = useState<
    SenderBlockResponse | null
  >(null);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError(null);
    if (!pattern.trim()) {
      setFormError("Pattern is required.");
      return;
    }
    try {
      await addBlock.mutateAsync({
        pattern: pattern.trim(),
        kind,
        reason: reason.trim() ? reason.trim() : null,
      });
      setPattern("");
      setReason("");
    } catch (err) {
      setFormError(
        err instanceof Error ? err.message : "Failed to add block.",
      );
    }
  };

  const handleConfirmRemove = async () => {
    if (!pendingRemove) return;
    try {
      await removeBlock.mutateAsync(pendingRemove.id);
    } finally {
      setPendingRemove(null);
    }
  };

  return (
    <div className="mx-auto w-full max-w-6xl">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold text-neutral-900">
          Spam blocklist
        </h1>
        <p className="mt-1 text-sm text-neutral-600">
          Sender patterns that bypass ticket creation. Populated by the
          mark-spam flow; admins can add or remove patterns here.
        </p>
      </header>

      <Card padding="md" className="mb-6">
        <h2 className="text-base font-semibold text-neutral-900">
          Add block
        </h2>
        <form onSubmit={handleSubmit} className="mt-3 space-y-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Input
              id="spam-add-pattern"
              label="Pattern"
              value={pattern}
              placeholder="spammer@example.com or example.com"
              onChange={(e) => setPattern(e.target.value)}
              required
            />
            <Select
              id="spam-add-kind"
              label="Kind"
              options={KIND_OPTIONS}
              value={kind}
              onChange={(e) => setKind(e.target.value as SenderBlockKind)}
            />
            <Input
              id="spam-add-reason"
              label="Reason"
              value={reason}
              placeholder="Optional context"
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
          {formError && (
            <Alert variant="error" title="Could not add block">
              {formError}
            </Alert>
          )}
          <div className="flex justify-end">
            <Button
              type="submit"
              variant="primary"
              size="sm"
              isLoading={addBlock.isPending}
            >
              Add
            </Button>
          </div>
        </form>
      </Card>

      {error && (
        <Alert variant="error" title="Failed to load blocks" className="mb-4">
          {error instanceof Error ? error.message : "Unknown error."}
        </Alert>
      )}

      <Card padding="none">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-neutral-200">
            <thead className="bg-neutral-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                  Pattern
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                  Kind
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                  Reason
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                  Blocked by
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                  Created
                </th>
                <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide text-neutral-500">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100 bg-white">
              {isLoading && (
                <tr>
                  <td colSpan={6} className="px-3 py-8 text-center">
                    <Spinner size="md" />
                  </td>
                </tr>
              )}
              {!isLoading && (data ?? []).length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-3 py-8 text-center text-sm text-neutral-500"
                  >
                    No senders are currently blocked.
                  </td>
                </tr>
              )}
              {(data ?? []).map((row) => (
                <SpamBlockRow
                  key={row.id}
                  row={row}
                  onRemove={(b) => setPendingRemove(b)}
                  isRemoving={removeBlock.isPending}
                />
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Modal
        isOpen={pendingRemove !== null}
        onClose={() => setPendingRemove(null)}
        title="Remove sender block?"
      >
        <p className="text-sm text-neutral-700">
          Future messages from{" "}
          <span className="font-mono">{pendingRemove?.pattern}</span> will
          again be able to create tickets.
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setPendingRemove(null)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            size="sm"
            className="bg-red-600 hover:bg-red-700 active:bg-red-800"
            onClick={() => void handleConfirmRemove()}
            isLoading={removeBlock.isPending}
          >
            Remove
          </Button>
        </div>
      </Modal>
    </div>
  );
}

export default AdminSpamPage;
